/**
 * Notification Sound Utility
 * 
 * Provides reliable audio playback for in-app notification alerts while
 * respecting browser autoplay policies by attaching an interaction listener
 * to unlock audio playback.
 */

let cachedAudio: HTMLAudioElement | null = null;
let audioContext: AudioContext | null = null;
let isAudioUnlocked = false;

export function resetAudioCache(): void {
  cachedAudio = null;
  audioContext = null;
  isAudioUnlocked = false;
  cleanupListeners();
  setupListeners();
}

function getAudioInstance(): HTMLAudioElement | null {
  if (typeof window === 'undefined' || !window.Audio) return null;
  if (cachedAudio) return cachedAudio;
  
  try {
    cachedAudio = new Audio('/sounds/ding.wav');
    return cachedAudio;
  } catch {
    return null;
  }
}

function unlockAudio() {
  if (isAudioUnlocked) return;
  if (typeof window === 'undefined') return;

  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx && !audioContext) {
      audioContext = new AudioCtx();
    }
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        isAudioUnlocked = true;
        cleanupListeners();
      }).catch(() => {
        // AudioContext resume failed
      });
    } else if (audioContext && audioContext.state === 'running') {
      isAudioUnlocked = true;
      cleanupListeners();
    }
  } catch {
    // Ignore AudioContext errors in unsupported/restricted environments
  }
}

function cleanupListeners() {
  if (typeof window !== 'undefined') {
    window.removeEventListener('pointerdown', unlockAudio);
    window.removeEventListener('keydown', unlockAudio);
  }
}

function setupListeners() {
  if (typeof window !== 'undefined') {
    window.addEventListener('pointerdown', unlockAudio, { passive: true, once: false });
    window.addEventListener('keydown', unlockAudio, { passive: true, once: false });
  }
}

// Attach passive unlock listeners on module load in browser context
setupListeners();

function isSoundEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('sound_notifications_enabled') !== 'false';
  } catch {
    return true;
  }
}

/**
 * Play the notification sound (ding.wav).
 */
export function playNotificationSound(): void {
  if (!isSoundEnabled()) {
    return;
  }

  const audio = getAudioInstance();
  if (!audio) return;

  try {
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        // Autoplay policy or media loading error
        console.warn('[NotificationSound] Audio playback failed or blocked by autoplay:', err);
      });
    }
  } catch (err) {
    console.warn('[NotificationSound] Failed to trigger sound:', err);
  }
}
